import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { ThemeProvider } from "@mui/material";
import { getClientThemes, type ClientTheme } from "@/api/client.service";
import { createAppTheme, defaultBrandColors, type BrandColors } from "@/theme";
import { useAuth } from "@/context/AuthContext";

const STORAGE_KEY = "brandTheme";

type CachedBrand = {
  clientId: string;
  themes: ClientTheme[];
};

interface BrandThemeContextType {
  /** Cores em uso; `null` quando o tema padrão davinTI está ativo. */
  brandColors: BrandColors | null;
  /** Recarrega as cores do cliente logado (usado após salvar a configuração). */
  refreshBrandTheme: () => Promise<void>;
}

const BrandThemeContext = createContext<BrandThemeContextType | null>(null);

/**
 * O back cadastra `light` e `dark` separadamente, mas o web opera só em modo
 * claro — a linha `dark` existe para o app mobile. Sem `themes[0]` de fallback:
 * ele aplicaria cores escuras como se fossem claras num cliente que só
 * cadastrou a linha dark.
 */
const toBrandColors = (themes: ClientTheme[]): BrandColors | null => {
  const theme = themes.find((t) => t.theme === "light");
  if (!theme) return null;

  return {
    primary: theme.primary_color,
    secondary: theme.secondary_color,
    background: theme.background_color,
    text: theme.text_color,
  };
};

const readCache = (clientId: string): ClientTheme[] | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const cached = JSON.parse(raw) as CachedBrand;
    if (cached.clientId !== clientId || !Array.isArray(cached.themes)) return null;
    return cached.themes;
  } catch {
    return null;
  }
};

const writeCache = (clientId: string, themes: ClientTheme[]) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ clientId, themes } satisfies CachedBrand));
  } catch {
    // Storage indisponível (modo privado/quota) — o tema ainda funciona, só não persiste.
  }
};

const clearCache = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // idem
  }
};

/**
 * Aplica as cores do cliente selecionado ao tema do MUI, do mesmo jeito que o
 * app mobile faz. As linhas de tema ficam em cache no localStorage para que a
 * marca já apareça no primeiro render após um refresh, antes da resposta da API.
 */
export const BrandThemeProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const clientId = user?.loggedClient?.id ?? null;

  const [themes, setThemes] = useState<ClientTheme[] | null>(() =>
    clientId ? readCache(clientId) : null,
  );

  // Ignora respostas de um cliente que já não é o selecionado.
  const requestIdRef = useRef(0);

  const loadBrandTheme = useCallback(async (id: string | null) => {
    const requestId = ++requestIdRef.current;

    if (!id) {
      setThemes(null);
      clearCache();
      return;
    }

    const cached = readCache(id);
    if (cached) setThemes(cached);

    try {
      const fetched = await getClientThemes(id);
      if (requestId !== requestIdRef.current) return;

      if (fetched?.length) {
        setThemes(fetched);
        writeCache(id, fetched);
      } else {
        setThemes(null);
        clearCache();
      }
    } catch {
      // Falha de rede: mantém o cache (ou o padrão) em vez de piscar o tema.
      if (requestId === requestIdRef.current && !cached) setThemes(null);
    }
  }, []);

  useEffect(() => {
    loadBrandTheme(clientId);
  }, [clientId, loadBrandTheme]);

  const refreshBrandTheme = useCallback(() => loadBrandTheme(clientId), [clientId, loadBrandTheme]);

  const brandColors = useMemo(() => (themes ? toBrandColors(themes) : null), [themes]);

  const theme = useMemo(() => createAppTheme(brandColors ?? defaultBrandColors), [brandColors]);

  const value = useMemo(() => ({ brandColors, refreshBrandTheme }), [brandColors, refreshBrandTheme]);

  return (
    <BrandThemeContext.Provider value={value}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </BrandThemeContext.Provider>
  );
};

export const useBrandTheme = () => {
  const context = useContext(BrandThemeContext);
  if (!context) throw new Error("useBrandTheme must be used within BrandThemeProvider");
  return context;
};
